// Copyright (c) Docugami, Inc. All rights reserved.

import { ColDef, ColGroupDef, Column } from "ag-grid-community";
import { IReportRow } from './AgGridReportTable';
import { EntityState } from "@reduxjs/toolkit";
import { ReportEntry } from "networking/reportsApi";

export type ReorderingResult = {
    didReorder: boolean;
    reorderedEntries: string[];
    groupChange?: { type: 'add' | 'remove'; sourceEntryId: string; targetGroupId?: string };
}

function isSingleColDef(obj: ColDef<IReportRow> | ColGroupDef<IReportRow>): obj is ColDef<IReportRow> {
    return (obj as ColDef<IReportRow>).colId !== undefined && (typeof (obj as ColDef<IReportRow>).colId) === 'string';
}

function isGroupColDef(obj: ColDef<IReportRow> | ColGroupDef<IReportRow>): obj is ColGroupDef<IReportRow> {
    return (obj as ColGroupDef<IReportRow>).headerName !== undefined && (typeof (obj as ColGroupDef<IReportRow>).headerName) === 'string' &&
        (obj as ColGroupDef<IReportRow>).children !== undefined;
}

export function orderEntriesAfterColumnMove({
    draggedColumns,
    destinationIndex,
    columns,
    entryIds,
    entries,
    isMovingColumnGroup,
}: {
    draggedColumns: Column<IReportRow>[];
    destinationIndex: number;
    columns: (ColDef<IReportRow> | ColGroupDef<IReportRow>)[];
    entryIds: string[];
    entries: EntityState<ReportEntry>;
    isMovingColumnGroup: boolean;
}): ReorderingResult {
    let flattenedColumns: ColDef<IReportRow>[] = [];
    for (let i = 0; i < columns.length; i++) {
        const c = columns[i];
        if (isSingleColDef(c)) {
            flattenedColumns.push(c);
        } else if (isGroupColDef(c)) {
            flattenedColumns = [...flattenedColumns, ...c.children];
        }
    }

    const draggedColIds = draggedColumns.map(col => col.getColId());
    const sourceColumns = flattenedColumns.filter(c => c.colId && draggedColIds.includes(c.colId));

    // Minus 1 here because the first column is the "header" column containing document names, and we
    // don't allow swapping with that.
    const targetColumn = flattenedColumns[destinationIndex - 1];

    if (sourceColumns.length === 0 || !targetColumn) {
        return {
            didReorder: false,
            reorderedEntries: [],
        };
    }

    const sourceColIds = sourceColumns.map(col => col.colId);
    const { colId: targetColId } = targetColumn;

    if (sourceColIds.every(id => typeof id === 'string') && typeof targetColId === 'string') {
        const sourceIndices = sourceColIds.map(id => entryIds.indexOf(id!));
        const targetIndex = entryIds.indexOf(targetColId);

        if (sourceIndices.every(index => index !== -1) && targetIndex !== -1) {
            let orderedEntries = [...entryIds];
            const movedEntries = sourceIndices.map(index => orderedEntries[index]);

            for (let i = sourceIndices.length - 1; i >= 0; i--) {
                orderedEntries.splice(sourceIndices[i], 1);
            }

            orderedEntries.splice(targetIndex, 0, ...movedEntries);

            const sourceEntry = entries.entities[sourceColIds[0]!];
            const targetEntry = entries.entities[targetColId];

            let groupChange;

            // Note that when moving the entire column group it's impossible to register a group change.
            if (!isMovingColumnGroup && sourceEntry && targetEntry && sourceEntry.groupId !== targetEntry.groupId) {
                if (!targetEntry.groupId) {
                    groupChange = {
                        type: 'remove' as const,
                        sourceEntryId: sourceEntry.id,
                        targetGroupId: sourceEntry.groupId,
                    };
                } else {
                    groupChange = {
                        type: 'add' as const,
                        sourceEntryId: sourceEntry.id,
                        targetGroupId: targetEntry.groupId,
                    };
                }
            }

            return {
                didReorder: true,
                reorderedEntries: orderedEntries,
                groupChange,
            };
        }
    }

    return {
        didReorder: false,
        reorderedEntries: [],
    };
}


